// Tag audit history section for DocumentDetail's Activity tab.
// FR (drthe0, 2026-06-03).
//
// Renders the most recent TagAuditReport for a document, with the
// option to re-run, view history, and clear stored history. Pulls
// from GET /documents/:id/tag-audits and triggers POST/DELETE on the
// same path.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eraser, Play, RefreshCw, Zap } from 'lucide-react';
import type { TagAuditReport, TagAuditAttribution } from '@captain-tasko/shared';
import { apiFetch, apiList, apiSingle } from '../lib/api.js';
import { toast } from './Toast.js';

interface TagAuditTaskRow {
  id: string;
  status: string;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  resultMessage: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  report: TagAuditReport | null;
}

function formatRelative(iso: string): string {
  try {
    const dt = new Date(iso).getTime();
    const seconds = Math.max(0, Math.round((Date.now() - dt) / 1000));
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    return `${days}d ago`;
  } catch { return iso; }
}

function attributionBadge(attr: TagAuditAttribution): { label: string; color: string; bg: string } {
  switch (attr.kind) {
    case 'rule':     return { label: `rule · ${attr.ruleName}`, color: 'var(--accent-primary)', bg: 'color-mix(in srgb, var(--accent-primary) 14%, var(--surface-raised))' };
    case 'workflow': return { label: `workflow · ${attr.workflowName}`, color: 'var(--success-primary)', bg: 'color-mix(in srgb, var(--success-primary) 14%, var(--surface-raised))' };
    case 'operator': return { label: 'operator', color: '#8B5CF6', bg: 'color-mix(in srgb, #8B5CF6 14%, var(--surface-raised))' };
    case 'system':   return { label: `system · ${attr.reason}`, color: '#F59E0B', bg: 'color-mix(in srgb, #F59E0B 14%, var(--surface-raised))' };
    case 'unknown':  return { label: 'unknown', color: 'var(--text-muted)', bg: 'var(--surface-sunken)' };
  }
}

export function TagAuditSection({ documentId }: { documentId: string }) {
  const qc = useQueryClient();
  const [showHistory, setShowHistory] = useState(false);

  const history = useQuery({
    queryKey: ['tag-audits', documentId],
    queryFn: () => apiList<TagAuditTaskRow>(`/documents/${documentId}/tag-audits`),
  });

  const run = useMutation({
    mutationFn: () => apiSingle<{ runId: string; report: TagAuditReport; completedAt: string }>(
      `/documents/${documentId}/tag-audits`,
      { method: 'POST' },
    ),
    onSuccess: (data) => {
      toast.success(`Tag audit done — ${data.report.events.length} event${data.report.events.length === 1 ? '' : 's'}`);
      qc.invalidateQueries({ queryKey: ['tag-audits', documentId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // 0.1.19 — "Re-run and apply" variant. POSTs with applyChanges:true,
  // which re-fires applyRulesToDocument on the server. Side-effects:
  // - tagging rules re-evaluate against the current sidecar; tags are
  //   added/removed to match rule output
  // - the 0.1.18 tail-projection (projectToCustomProperties) fires at
  //   the end of applyRulesToDocument, repopulating
  //   documents.customProperties from sidecar.frontmatter
  // So we also invalidate the document detail + CP provenance queries
  // so chips + values refresh without a hard reload.
  const runAndApply = useMutation({
    mutationFn: () => apiSingle<{ runId: string; report: TagAuditReport; completedAt: string }>(
      `/documents/${documentId}/tag-audits`,
      { method: 'POST', body: JSON.stringify({ applyChanges: true }) },
    ),
    onSuccess: (data) => {
      const ar = data.report.appliedRules;
      const detail = ar
        ? `${ar.tagsApplied} tag${ar.tagsApplied === 1 ? '' : 's'} applied, ${ar.tagsRemoved} removed`
        : `${data.report.events.length} event${data.report.events.length === 1 ? '' : 's'}`;
      toast.success(`Tag audit + apply done — ${detail}`);
      qc.invalidateQueries({ queryKey: ['tag-audits', documentId] });
      // Refresh dependent views so the operator sees the new state.
      qc.invalidateQueries({ queryKey: ['document', documentId] });
      qc.invalidateQueries({ queryKey: ['document-detail', documentId] });
      qc.invalidateQueries({ queryKey: ['cp-provenance', documentId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const clear = useMutation({
    mutationFn: () => apiFetch<{ deletedCount: number }>(
      `/documents/${documentId}/tag-audits`,
      { method: 'DELETE' },
    ),
    onSuccess: () => {
      toast.success('Tag audit history cleared');
      qc.invalidateQueries({ queryKey: ['tag-audits', documentId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rows = history.data?.data ?? [];
  const latest = rows.find((r) => r.status === 'completed' && r.report);
  const report = latest?.report ?? null;

  return (
    <section
      style={{
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: 20,
        marginBottom: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
            Tag history
          </h3>
          {latest && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              Last run {formatRelative(latest.completedAt ?? latest.createdAt)} · {rows.length} run{rows.length === 1 ? '' : 's'} in history
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => run.mutate()}
            disabled={run.isPending || runAndApply.isPending}
            title="Scan the audit log and produce a fresh tag attribution report (read-only)"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            {run.isPending ? <RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} aria-hidden /> : <Play size={13} aria-hidden />}
            {run.isPending ? 'Running…' : (rows.length === 0 ? 'Run audit' : 'Re-run')}
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              if (!confirm('Re-run audit AND re-fire tagging rules?\n\nThis re-applies tags + container assignments per current rules and re-projects custom properties from frontmatter. Use this to bring a stale document back in sync after rule or bundle changes.')) return;
              runAndApply.mutate();
            }}
            disabled={run.isPending || runAndApply.isPending}
            title="Re-run audit AND re-fire tagging rules (applies tags, fires CP projection)"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            {runAndApply.isPending ? <RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} aria-hidden /> : <Zap size={13} aria-hidden />}
            {runAndApply.isPending ? 'Applying…' : 'Re-run & apply'}
          </button>
          {rows.length > 0 && (
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                if (confirm(`Clear ${rows.length} tag audit run${rows.length === 1 ? '' : 's'} from history?`)) clear.mutate();
              }}
              title="Delete every stored tag audit run for this document"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--danger-primary)' }}
            >
              <Eraser size={13} aria-hidden /> Clear
            </button>
          )}
        </div>
      </div>

      {!history.isLoading && rows.length === 0 && (
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
          No tag audit has been run for this document yet. Click <strong>Run audit</strong> to attribute each current tag to the rule, workflow, or operator that applied it.
        </div>
      )}

      {report && (
        <>
          {report.summary.truncated && (
            <div style={{ background: 'color-mix(in srgb, #F59E0B 14%, var(--surface-raised))', color: '#854F0B', padding: '6px 10px', borderRadius: 4, fontSize: 12, marginBottom: 10 }}>
              History truncated — scan hit the cap at {report.summary.eventsScanned} events. Older events not shown.
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
            <span><strong>{report.summary.currentTagCount}</strong> current tag{report.summary.currentTagCount === 1 ? '' : 's'}</span>
            <span><strong>{report.events.length}</strong> event{report.events.length === 1 ? '' : 's'}</span>
            {Object.entries(report.summary.appliedByRule).length > 0 && (
              <span><strong>{Object.entries(report.summary.appliedByRule).length}</strong> rule{Object.entries(report.summary.appliedByRule).length === 1 ? '' : 's'} fired</span>
            )}
            {report.summary.appliedByOperator > 0 && (
              <span><strong>{report.summary.appliedByOperator}</strong> operator edit{report.summary.appliedByOperator === 1 ? '' : 's'}</span>
            )}
          </div>

          {/* 0.1.19 — when the latest run used "Re-run & apply", surface
              the rule engine's summary so the operator sees what the
              apply pass actually changed (vs. the diff-only summary
              above which reports on the audit-log history). */}
          {report.appliedRules && (
            <div style={{
              background: 'color-mix(in srgb, var(--accent-primary) 8%, var(--surface-raised))',
              border: '1px solid color-mix(in srgb, var(--accent-primary) 30%, transparent)',
              borderRadius: 4,
              padding: '6px 10px',
              fontSize: 12,
              color: 'var(--text-muted)',
              marginBottom: 12,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 12,
            }}>
              <span style={{ color: 'var(--accent-primary)', fontWeight: 600 }}>
                <Zap size={11} aria-hidden style={{ verticalAlign: -1, marginRight: 3 }} />
                Apply pass:
              </span>
              <span><strong>{report.appliedRules.rulesEvaluated}</strong> rule{report.appliedRules.rulesEvaluated === 1 ? '' : 's'} evaluated</span>
              <span><strong>{report.appliedRules.rulesMatched}</strong> matched</span>
              <span><strong>{report.appliedRules.tagsApplied}</strong> tag{report.appliedRules.tagsApplied === 1 ? '' : 's'} applied</span>
              <span><strong>{report.appliedRules.tagsRemoved}</strong> removed</span>
              {report.appliedRules.containerSet && (
                <span>container set</span>
              )}
            </div>
          )}

          <table style={{ width: '100%', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', color: 'var(--text-muted)', padding: '4px 6px' }}>Action</th>
                <th style={{ textAlign: 'left', color: 'var(--text-muted)', padding: '4px 6px' }}>Tag</th>
                <th style={{ textAlign: 'left', color: 'var(--text-muted)', padding: '4px 6px' }}>Source</th>
                <th style={{ textAlign: 'left', color: 'var(--text-muted)', padding: '4px 6px' }}>When</th>
              </tr>
            </thead>
            <tbody>
              {report.events.map((e, i) => {
                const badge = attributionBadge(e.attribution);
                const isApply = e.action.includes('apply') || e.action === 'document.tag_add';
                return (
                  <tr key={i} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: '6px', color: isApply ? 'var(--success-primary)' : 'var(--danger-primary)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                      {isApply ? '+ applied' : '− removed'}
                    </td>
                    <td style={{ padding: '6px' }}><code style={{ fontFamily: 'var(--font-mono)' }}>{e.tagName}</code></td>
                    <td style={{ padding: '6px' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '1px 8px',
                          borderRadius: 999,
                          fontSize: 10,
                          fontWeight: 600,
                          background: badge.bg,
                          color: badge.color,
                          border: `1px solid color-mix(in srgb, ${badge.color} 40%, transparent)`,
                        }}
                        title={e.rawMessage ?? ''}
                      >
                        {badge.label}
                      </span>
                      {e.actorName && (
                        <span style={{ marginLeft: 6, color: 'var(--text-muted)', fontSize: 11 }}>by {e.actorName}</span>
                      )}
                    </td>
                    <td style={{ padding: '6px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }} title={new Date(e.at).toLocaleString()}>
                      {formatRelative(e.at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {rows.length > 1 && (
        <details style={{ marginTop: 12 }} open={showHistory} onToggle={(e) => setShowHistory((e.target as HTMLDetailsElement).open)}>
          <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)' }}>
            Show prior runs ({rows.length - 1})
          </summary>
          <table style={{ width: '100%', fontSize: 11, marginTop: 8 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', color: 'var(--text-muted)', padding: '4px 6px' }}>Run</th>
                <th style={{ textAlign: 'left', color: 'var(--text-muted)', padding: '4px 6px' }}>Status</th>
                <th style={{ textAlign: 'left', color: 'var(--text-muted)', padding: '4px 6px' }}>Result</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(1).map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: '4px 6px', color: 'var(--text-muted)' }} title={new Date(r.createdAt).toLocaleString()}>
                    {formatRelative(r.createdAt)}
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    <code style={{ fontFamily: 'var(--font-mono)', fontSize: 10 }}>{r.status}</code>
                  </td>
                  <td style={{ padding: '4px 6px', color: r.status === 'failed' ? 'var(--danger-primary)' : 'var(--text-muted)' }}>
                    {r.resultMessage ?? r.errorMessage ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </section>
  );
}
